import { useMediaDeviceSelect, useRoomContext, useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { twMerge } from 'tailwind-merge';
import { useCallback } from 'react';
import { AudioSelectButton } from './buttons/AudioSelectButton';
import { MuteButton } from './buttons/MuteButton';
import { EndButton } from './buttons/EndButton';
import './locales/TutorBotVoiceAgent/audioControlBar-en.json';
import { getDeviceIdFromDropdownItemKey } from './utils';

type Props = {
    className?: string;
    onEndPress?: () => void;
};

export function AudioControlBar({ className, onEndPress }: Props) {
    const room = useRoomContext();
    const {
        activeDeviceId: activeInputDeviceId,
        setActiveMediaDevice: setActiveInputDevice,
        devices: inputDevices,
    } = useMediaDeviceSelect({
        kind: 'audioinput',
    });

    const {
        activeDeviceId: activeOutputDeviceId,
        setActiveMediaDevice: setActiveOutputDevice,
        devices: outputDevices,
    } = useMediaDeviceSelect({
        kind: 'audiooutput',
    });

    const {
        enabled: microphoneEnabled,
        pending: microphonePending,
        toggle: toggleMicrophone,
    } = useTrackToggle({
        source: Track.Source.Microphone,
        initialState: true,
    });

    const handleDeviceSelect = useCallback(
        (selectedDeviceKeys: string[]) => {
            const inputDeviceKey = selectedDeviceKeys.find(
                deviceKey =>
                    inputDevices.some(device => device.deviceId === getDeviceIdFromDropdownItemKey(deviceKey)) &&
                    getDeviceIdFromDropdownItemKey(deviceKey) !== activeInputDeviceId,
            );
            const outputDeviceKey = selectedDeviceKeys.find(
                deviceKey =>
                    outputDevices.some(device => device.deviceId === getDeviceIdFromDropdownItemKey(deviceKey)) &&
                    getDeviceIdFromDropdownItemKey(deviceKey) !== activeOutputDeviceId,
            );

            if (inputDeviceKey) {
                setActiveInputDevice(getDeviceIdFromDropdownItemKey(inputDeviceKey));
            }

            if (outputDeviceKey) {
                setActiveOutputDevice(getDeviceIdFromDropdownItemKey(outputDeviceKey));
            }
        },
        [
            inputDevices,
            setActiveInputDevice,
            setActiveOutputDevice,
            outputDevices,
            activeInputDeviceId,
            activeOutputDeviceId,
        ],
    );

    return (
        <AudioControlBarPresentation
            className={className}
            onDeviceSelect={handleDeviceSelect}
            onMutePress={() => toggleMicrophone()}
            onEndPress={onEndPress || (() => room.disconnect())}
            microphoneEnabled={microphoneEnabled}
            microphonePending={microphonePending}
            activeInputDeviceId={activeInputDeviceId}
            activeOutputDeviceId={activeOutputDeviceId}
            inputDevices={inputDevices}
            outputDevices={outputDevices}
        />
    );
}

export function AudioControlBarPresentation({
    className,
    onDeviceSelect,
    onMutePress,
    onEndPress,
    microphoneEnabled,
    microphonePending,
    activeInputDeviceId,
    activeOutputDeviceId,
    inputDevices,
    outputDevices,
}: {
    className?: string;
    onDeviceSelect: (deviceIds: string[]) => void;
    onMutePress: () => void;
    onEndPress: () => void;
    microphoneEnabled: boolean;
    microphonePending: boolean;
    activeInputDeviceId: string;
    activeOutputDeviceId: string;
    outputDevices: MediaDeviceInfo[];
    inputDevices: MediaDeviceInfo[];
}) {
    return (
        <div className={twMerge('flex w-full justify-center gap-2.5', className)}>
            <AudioSelectButton
                activeInputDeviceId={activeInputDeviceId}
                onSelect={onDeviceSelect}
                inputDevices={inputDevices}
                activeOutputDeviceId={activeOutputDeviceId}
                outputDevices={outputDevices}
            />
            <MuteButton onPress={onMutePress} enabled={microphoneEnabled} pending={microphonePending} />
            <EndButton onPress={onEndPress} />
        </div>
    );
}
